/**
 * Shared presentation constants for the /reranker grid and table views so
 * flag colours, feature order and cover URLs stay identical between them.
 */
import type { AffinityBookRow, AffinityFlag, AffinityScoreType } from '../services/api';

export const FLAG_STYLES: Record<AffinityFlag, string> = {
  content_blocked: 'bg-red-50 text-red-700 border-red-200',
  owned: 'bg-amber-50 text-amber-700 border-amber-200',
  age_mismatch: 'bg-orange-50 text-orange-700 border-orange-200',
  off_genre: 'bg-sky-50 text-sky-700 border-sky-200',
  vector_missing: 'bg-gray-100 text-gray-600 border-gray-200',
};

export const FEATURE_ORDER = [
  'cf_cosine',
  'desc_cosine',
  'genre_overlap',
  'genre_match_level',
  'theme_overlap',
  'log_popularity',
  'pop_proximity',
  'audience_match',
  'author_in_history',
  'series_started',
  'is_next_volume',
  'is_canadian_author',
  'days_since_pub',
  'language_match',
  'price_proximity',
  'format_match',
  'page_count_proximity',
  'sales_momentum',
  'in_series',
];

/**
 * Maps a score to a 0–100 bar width. Probability and similarity scores are
 * already in [0, 1]; rank scores are unbounded, so they are scaled between
 * the lowest and highest score in the list being shown.
 */
export function scoreBarScale(
  rows: AffinityBookRow[],
  scoreType: AffinityScoreType | undefined
): (score: number) => number {
  const clamp = (pct: number) => Math.max(0, Math.min(100, Math.round(pct)));
  if (scoreType !== 'rank' || rows.length === 0) {
    return (score) => clamp(score * 100);
  }
  const scores = rows.map((row) => row.score);
  const min = Math.min(...scores);
  const span = Math.max(...scores) - min;
  return (score) => (span > 0 ? clamp(((score - min) / span) * 100) : 100);
}

/** Same Indigo image service BookGrid uses; `width` picks thumbnail vs card size. */
export function coverUrl(row: AffinityBookRow, width = 80): string {
  const id = (row.sku || row.book_id).replace(/-/g, '');
  const height = Math.round(width * 1.5);
  return `https://dynamic.indigoimages.ca/v1/books/books/${id}/1.jpg?width=${width}&maxHeight=${height}&quality=85`;
}
